import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { api, ApiError } from '../api';
import { EVENTS_KEY, type EventView } from '../events/labels';
import { useSystems, type SystemItem } from '../settings/systems';
import { packlistKey, type PacklistView } from './types';

/** The block of the event's system (ours, with a block) when it is not on the packlist yet; else null. */
export function systemBlockToSuggest(systems: readonly SystemItem[], systemId: string | null | undefined, packlist: PacklistView | undefined): SystemItem | null {
  const system = systems.find((s) => s.id === systemId);
  if (!system || system.kind !== 'own' || !system.blockId || !packlist) return null;
  const inserted = packlist.lines.some((l) => l.contributions.some((c) => c.source === 'block' && c.sourceId === system.blockId));
  return inserted ? null : system;
}

/**
 * Owner remark 3/13: a system of ours may point to a block ("System nagłośnieniowy"); the packlist offers to put
 * it in with one click until it is there.
 */
export function SystemBlockHint({ eventId, packlist, canApply }: { eventId: string; packlist: PacklistView | undefined; canApply: boolean }) {
  const queryClient = useQueryClient();
  const event = useQuery({ queryKey: [...EVENTS_KEY, 'one', eventId], queryFn: () => api<EventView>(`/api/events/${eventId}`) });
  const systems = useSystems().data?.systems ?? [];
  const system = systemBlockToSuggest(systems, event.data?.systemId, packlist);
  const apply = useMutation({
    mutationFn: () => withConflictConfirm((confirmConflicts) => api<PacklistView>(`/api/packlists/${eventId}/apply-block`, { method: 'POST', body: { blockId: system!.blockId, ...(confirmConflicts ? { confirmConflicts } : {}) } })),
    onSuccess: (view) => {
      if (view) queryClient.setQueryData(packlistKey(eventId), view);
    },
  });
  if (!system || !canApply) return null;
  return (
    <p className="notice system-block-hint" role="status">
      System „{system.name}” ma blok „{system.blockName ?? 'bez nazwy'}”.{' '}
      <button className="btn small" type="button" disabled={apply.isPending} onClick={() => apply.mutate()}>
        Wstaw blok do sprzętówki
      </button>
      {apply.error && <span className="field-error"> {apply.error instanceof ApiError ? apply.error.message : 'Nie udało się wstawić bloku'}</span>}
    </p>
  );
}
